import { useState } from "react";
import DOMPurify from "dompurify";
import { Trash2, Send } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { RichTextEditor, type MentionSuggestion } from "@/components/ui/RichTextEditor";
import { getRoleLabel } from "@/utils/roleLabel";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import {
  useEmployeeComments,
  useCreateEmployeeComment,
  useDeleteEmployeeComment,
} from "@/features/finances/hooks/useEmployeeComments";

interface Props {
  employeeId: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["start"],
  });
}

function CommentBody({ content }: { content: string }) {
  const { data: allUsers = [] } = useInternalUsers();
  const names = allUsers.map((u) => u.full_name);
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-700 dark:text-gray-300"
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
      />
    );
  }
  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

export function EmployeeCommentThread({ employeeId }: Props) {
  const { data: comments = [], isLoading } = useEmployeeComments(employeeId);
  const { mutate: createComment, isPending: sending } = useCreateEmployeeComment(employeeId);
  const { mutate: deleteComment } = useDeleteEmployeeComment(employeeId);
  const currentUser = useAuthStore((s) => s.user);
  const { data: allUsers = [] } = useInternalUsers();

  const [draft, setDraft] = useState("");
  const [mentionIds, setMentionIds] = useState<string[]>([]);

  const mentionSuggestions: MentionSuggestion[] = allUsers.map((u) => ({
    id: u.id,
    name: u.full_name,
    role: u.role,
    is_ceo: u.is_ceo,
  }));

  function handleSend() {
    const content = draft.trim();
    if (!content) return;
    createComment(
      { content, mentionIds },
      {
        onSuccess: () => {
          setDraft("");
          setMentionIds([]);
        },
      }
    );
  }

  const canDelete = (authorId: string | null) =>
    currentUser?.id === authorId ||
    currentUser?.role === "director" ||
    !!currentUser?.is_ceo;

  if (isLoading) return <div className="flex justify-center py-4"><Spinner size="sm" /></div>;

  return (
    <div className="flex flex-col gap-3">
      {comments.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          Brak komentarzy. Wpisz notatkę poniżej.
        </p>
      )}

      {comments.map((c) => (
        <div key={c.id} className="flex gap-2.5 group rounded-lg bg-white dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 px-3 py-2.5">
          <UserAvatar
            userId={c.author}
            fullName={c.author_name}
            profilePictureUrl={c.author_profile_picture_url}
            size="lg"
            className="mt-0.5 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {c.author_name}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {getRoleLabel(c.author_role, c.author_is_ceo)}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                {formatDate(c.created_at)}
              </span>
            </div>
            <CommentBody content={c.content} />
          </div>

          {canDelete(c.author) && (
            <button
              onClick={() => {
                if (confirm("Usunąć komentarz?")) deleteComment(c.id);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-red-500"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}

      {/* Composer */}
      <div className="flex gap-2 mt-1 border-t border-gray-100 dark:border-gray-700 pt-3 items-end">
        <div className="flex-1">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            disabled={sending}
            placeholder="Napisz notatkę… (wpisz @ aby oznaczyć)"
            mentionSuggestions={mentionSuggestions}
            onMentionIdsChange={setMentionIds}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          className="p-2 bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-40 self-end mb-0.5"
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}
